/**
 * Cotizador de créditos — pantalla propia (menú Productos).
 *
 * Cotiza cualquier tipo de crédito (Consumo / Emergente) en vivo con
 * simular_credito, muestra la tabla de amortización completa, y permite
 * originar la solicitud para un asociado (búsqueda por nombre / neo.id).
 * La cotización es imprimible (window.print) para compartir.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calculator, MagnifyingGlass, Printer, UserPlus, CheckCircle, X, FileText } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { imprimirPropuestaCredito } from '@/lib/propuestaCredito'

type TipoCredito = {
  id: string
  codigo: string
  nombre: string
  monto_minimo: number
  monto_maximo: number
  plazos_dias: number[]
  tasa: number
  periodicidad_tasa: string
  aprobacion_automatica: boolean
  redondea_aportacion?: boolean
}

type Cuota = {
  numero: number
  fecha: string
  capital: number
  interes: number
  total: number
  cuota_cobrada?: number
  aporte_aportaciones?: number
  saldo_post: number
}

type Plan = {
  num_cuotas: number
  fecha_primera_cuota: string
  fecha_ultima_cuota: string
  total_capital: number
  total_interes: number
  total_a_pagar: number
  total_cobrado?: number
  total_aporte_aportaciones?: number
  redondea_aportacion?: boolean
  cuotas: Cuota[]
}

type Asociado = { id: string; nombre_completo: string; no_asociado: string; neo_id: string | null; estado_asociado: string }

const fmtQ = (n: number) => 'Q' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtQ0 = (n: number) => 'Q' + Number(n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
const fmtFecha = (iso: string) => new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
const plazoLabel = (d: number, period: string) => (period === 'mensual' ? `${Math.round(d / 30)} meses` : `${d} días`)

export default function NeoCoreCotizadorPage() {
  const navigate = useNavigate()
  const [tipos, setTipos] = useState<TipoCredito[]>([])
  const [tipo, setTipo] = useState<TipoCredito | null>(null)
  const [monto, setMonto] = useState(5000)
  const [montoText, setMontoText] = useState('5000')
  const [plazo, setPlazo] = useState(180)
  const [plan, setPlan] = useState<Plan | null>(null)
  const [clienteNombre, setClienteNombre] = useState('')

  // Originación
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState<Asociado[]>([])
  const [asociado, setAsociado] = useState<Asociado | null>(null)
  const [proposito, setProposito] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const [errMsg, setErrMsg] = useState<string | null>(null)

  // Cargar tipos activos
  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('tipo_credito')
        .select('id, codigo, nombre, monto_minimo, monto_maximo, plazos_dias, tasa, periodicidad_tasa, aprobacion_automatica, redondea_aportacion')
        .eq('activo', true)
        .order('monto_maximo', { ascending: false })
      const list = (data ?? []) as unknown as TipoCredito[]
      setTipos(list)
      const consumo = list.find((t) => t.codigo === 'consumo') ?? list[0]
      if (consumo) {
        setTipo(consumo)
        const m0 = Math.min(consumo.monto_maximo, Math.max(consumo.monto_minimo, 5000))
        setMonto(m0); setMontoText(String(m0))
        setPlazo(consumo.plazos_dias[0])
      }
    })()
  }, [])

  const onTipo = (t: TipoCredito) => {
    setTipo(t)
    const m0 = Math.min(t.monto_maximo, Math.max(t.monto_minimo, 5000))
    setMonto(m0); setMontoText(String(m0))
    setPlazo(t.plazos_dias[0])
    setOkMsg(null); setErrMsg(null)
  }

  // Monto escribible: refleja el texto y clampa al rango al salir del campo.
  const onMontoText = (v: string) => {
    const d = v.replace(/[^\d]/g, '')
    setMontoText(d)
    setMonto(Number(d || 0))
  }
  const onMontoBlur = () => {
    if (!tipo) return
    const n = Number(montoText || 0)
    const clamped = Math.min(tipo.monto_maximo, Math.max(tipo.monto_minimo, n || tipo.monto_minimo))
    setMonto(clamped); setMontoText(String(clamped))
  }
  const onSlider = (v: number) => { setMonto(v); setMontoText(String(v)) }

  // Simular en vivo
  useEffect(() => {
    if (!tipo) return
    if (monto < tipo.monto_minimo || monto > tipo.monto_maximo) { setPlan(null); return }
    let cancel = false
    void (async () => {
      const { data, error } = await supabase.rpc('simular_credito', { p_tipo_credito_id: tipo.id, p_monto: monto, p_plazo_dias: plazo })
      if (cancel) return
      setPlan(error ? null : (data as Plan))
    })()
    return () => { cancel = true }
  }, [tipo, monto, plazo])

  // Búsqueda de asociado (debounce simple)
  const tRef = useRef<number | null>(null)
  const buscar = useCallback((texto: string) => {
    if (tRef.current) window.clearTimeout(tRef.current)
    tRef.current = window.setTimeout(async () => {
      const s = texto.trim()
      if (s.length < 2) { setResultados([]); return }
      const { data } = await supabase
        .from('asociado')
        .select('id, nombre_completo, no_asociado, neo_id, estado_asociado')
        .or(`nombre_completo.ilike.%${s}%,no_asociado.ilike.%${s}%,neo_id.ilike.%${s}%`)
        .eq('estado_asociado', 'activo')
        .limit(8)
      setResultados((data ?? []) as unknown as Asociado[])
    }, 250)
  }, [])

  const cuotaMensual = plan?.cuotas[0]?.cuota_cobrada ?? plan?.cuotas[0]?.total ?? 0
  const totalCobrado = plan?.total_cobrado ?? plan?.total_a_pagar ?? 0
  const redondea = !!(plan?.redondea_aportacion ?? tipo?.redondea_aportacion)

  const originar = async () => {
    if (!tipo || !asociado || !plan) return
    setSubmitting(true); setErrMsg(null); setOkMsg(null)
    try {
      const { data: sol, error: solErr } = await supabase.rpc('solicitar_credito_admin', {
        p_input: { asociado_id: asociado.id, tipo_credito_codigo: tipo.codigo, monto, plazo_dias: plazo, proposito: proposito.trim() || null },
      })
      if (solErr) throw solErr
      const sd = sol as { solicitud_id: string; estado: string }
      if (tipo.aprobacion_automatica) {
        if (sd.estado === 'aprobada') {
          const { error: desErr } = await supabase.rpc('desembolsar_credito_admin', { p_input: { solicitud_id: sd.solicitud_id } })
          if (desErr) throw desErr
        }
        setOkMsg(`Crédito desembolsado a ${asociado.nombre_completo}.`)
      } else {
        if (sd.estado !== 'aprobada') {
          const { error: apErr } = await supabase.rpc('aprobar_solicitud_credito_admin', { p_solicitud_id: sd.solicitud_id, p_nota: 'Originado desde Cotizador' })
          if (apErr) throw apErr
        }
        setOkMsg(`Solicitud creada para ${asociado.nombre_completo}. Falta que firme el pagaré en la app; luego desembolsás desde Originación.`)
      }
    } catch (e) {
      // Surfacear el mensaje real del RPC (los errores de supabase-js no son
      // instancias de Error, así que 'e instanceof Error' fallaba y mostraba
      // "error desconocido" tapando la razón real, p.ej. antigüedad mínima).
      const err = e as { message?: string; details?: string; hint?: string } | null
      const msg = err?.message || err?.details || err?.hint || (e instanceof Error ? e.message : '') || 'Error al originar'
      setErrMsg(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const tasaTxt = tipo ? `${(tipo.tasa * 100).toFixed(2)}% ${tipo.periodicidad_tasa} · tasa fija sobre el capital` : '—'

  // Propuesta formal (carta con marca) — window nueva + print/PDF.
  const printPropuesta = () => {
    if (!tipo || !plan) return
    imprimirPropuestaCredito({
      clienteNombre: (asociado?.nombre_completo ?? clienteNombre).trim(),
      productoNombre: tipo.nombre,
      monto,
      numCuotas: plan.num_cuotas,
      periodicidad: tipo.periodicidad_tasa,
      tasaPct: tipo.tasa * 100,
      cuotaPeriodo: cuotaMensual,
      totalInteres: plan.total_interes,
      totalAPagar: totalCobrado,
      totalAporte: plan.total_aporte_aportaciones,
      redondea,
      fechaPrimera: plan.fecha_primera_cuota,
      fechaUltima: plan.fecha_ultima_cuota,
      cuotas: plan.cuotas,
    })
  }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <style>{`@media print{
        .sidebar,.topbar,.sidebar-mobile-overlay,.sidebar-collapse-btn{display:none!important}
        .shell{display:block!important}
        .main,.content{padding:0!important;margin:0!important;gap:0!important}
        .no-print{display:none!important}
        .cotiz-grid{display:block!important}
        .cotiz-print{box-shadow:none!important;border:none!important;border-radius:0!important}
        .cotiz-print,.cotiz-print *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
        @page{margin:12mm}
      }`}</style>

      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <Calculator size={24} weight="duotone" color="var(--brand)" />
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>Cotizador de créditos</h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>Cotizá, mostrá la tabla de amortización y originá para un asociado.</p>
        </div>
      </div>

      <div className="cotiz-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 380px) 1fr', gap: 18, alignItems: 'start' }}>

        {/* ---------- Panel de controles ---------- */}
        <div className="no-print" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Producto */}
          <div style={card}>
            <p style={lbl}>Producto</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {tipos.map((t) => {
                const on = tipo?.codigo === t.codigo
                return (
                  <button key={t.codigo} onClick={() => onTipo(t)} style={{
                    all: 'unset', cursor: 'pointer', padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                    border: '1px solid', borderColor: on ? 'var(--brand)' : 'var(--hairline)',
                    background: on ? 'var(--brand)' : '#fff', color: on ? '#fff' : 'var(--ink-soft)',
                  }}>{t.nombre}</button>
                )
              })}
            </div>
          </div>

          {tipo && (
            <div style={card}>
              {/* Monto */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={lbl}>Monto</span>
                  <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--brand)' }}>Q</span>
                    <input
                      value={Number(montoText || 0).toLocaleString('en-US')}
                      onChange={(e) => onMontoText(e.target.value)}
                      onBlur={onMontoBlur}
                      inputMode="numeric"
                      aria-label="Monto del crédito"
                      style={{
                        width: 130, textAlign: 'right', fontSize: 22, fontWeight: 800, color: 'var(--brand)',
                        border: 'none', borderBottom: '2px solid var(--brand)', background: 'transparent',
                        outline: 'none', fontFamily: 'inherit', fontFeatureSettings: '"tnum"', padding: '0 0 2px',
                      }}
                    />
                  </div>
                </div>
                <input type="range" min={tipo.monto_minimo} max={tipo.monto_maximo} step={100} value={Math.min(tipo.monto_maximo, Math.max(tipo.monto_minimo, monto))}
                  onChange={(e) => onSlider(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--brand)', marginTop: 8 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted-foreground)' }}>
                  <span>{fmtQ0(tipo.monto_minimo)}</span><span>{fmtQ0(tipo.monto_maximo)}</span>
                </div>
              </div>
              {/* Plazo */}
              <div>
                <p style={lbl}>Plazo</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {tipo.plazos_dias.map((d) => {
                    const on = plazo === d
                    return (
                      <button key={d} onClick={() => setPlazo(d)} style={{
                        all: 'unset', cursor: 'pointer', padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                        border: '1px solid', borderColor: on ? 'var(--brand)' : 'var(--hairline)',
                        background: on ? 'var(--brand-soft, #ffe4d4)' : '#fff', color: on ? 'var(--brand-deep)' : 'var(--ink-soft)',
                      }}>{plazoLabel(d, tipo.periodicidad_tasa)}</button>
                    )
                  })}
                </div>
                <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '8px 0 0' }}>Tasa {tasaTxt}.</p>
              </div>

              {/* Nombre para la propuesta impresa */}
              <div style={{ marginTop: 16 }}>
                <p style={lbl}>Nombre para la propuesta</p>
                <input
                  value={asociado ? asociado.nombre_completo : clienteNombre}
                  onChange={(e) => setClienteNombre(e.target.value)}
                  disabled={!!asociado}
                  placeholder="Nombre del prospecto (opcional)"
                  style={{ width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--hairline)', fontSize: 13, fontFamily: 'inherit', background: asociado ? 'var(--bg)' : '#fff' }}
                />
                <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '6px 0 0' }}>Aparece en el encabezado de la propuesta (“Estimado…”).</p>
              </div>
            </div>
          )}

          {/* Originar para un asociado */}
          <div style={card}>
            <p style={lbl}>Originar para un asociado</p>
            {!asociado ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg)', borderRadius: 8, padding: '8px 10px', border: '1px solid var(--hairline)' }}>
                  <MagnifyingGlass size={14} color="var(--muted-foreground)" />
                  <input value={q} onChange={(e) => { setQ(e.target.value); buscar(e.target.value) }} placeholder="Nombre, neo.id o N.º asociado…"
                    style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--ink)' }} />
                </div>
                {resultados.length > 0 && (
                  <div style={{ marginTop: 8, border: '1px solid var(--hairline)', borderRadius: 8, overflow: 'hidden' }}>
                    {resultados.map((a) => (
                      <button key={a.id} onClick={() => { setAsociado(a); setResultados([]); setQ('') }} style={{
                        all: 'unset', cursor: 'pointer', display: 'block', width: '100%', padding: '9px 12px', borderBottom: '1px solid var(--line-soft)',
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{a.nombre_completo}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{a.neo_id ?? a.no_asociado}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'var(--brand-50, #fff3ec)', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--brand-200, var(--hairline))' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{asociado.nombre_completo}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{asociado.neo_id ?? asociado.no_asociado}</div>
                </div>
                <button onClick={() => { setAsociado(null); setOkMsg(null); setErrMsg(null) }} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--muted-foreground)' }}><X size={16} /></button>
              </div>
            )}

            {asociado && (
              <>
                <input value={proposito} onChange={(e) => setProposito(e.target.value)} placeholder="Propósito (opcional)"
                  style={{ marginTop: 10, width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--hairline)', fontSize: 13, fontFamily: 'inherit' }} />
                <button className="btn btn-primary" onClick={originar} disabled={submitting || !plan} style={{ marginTop: 10, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <UserPlus size={14} weight="bold" />
                  {submitting ? 'Originando…' : (tipo?.aprobacion_automatica ? `Aprobar y desembolsar ${fmtQ0(monto)}` : `Crear solicitud ${fmtQ0(monto)} (a firmar)`)}
                </button>
              </>
            )}
            {okMsg && (
              <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: 'rgba(15,138,87,.08)', color: 'var(--ok-ink, #0f8a57)', fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <CheckCircle size={16} weight="fill" /> <div>{okMsg}<br /><button onClick={() => navigate('/neocore/creditos/originacion')} style={{ background: 'none', border: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontWeight: 700 }}>Ir a Originación →</button></div>
              </div>
            )}
            {errMsg && <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: 'rgba(163,45,45,.08)', color: 'var(--danger, #a32d2d)', fontSize: 12.5 }}>{errMsg}</div>}
          </div>
        </div>

        {/* ---------- Cotización (imprimible) ---------- */}
        <div className="cotiz-print" style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ background: 'var(--brand)', color: '#fff', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800 }}>Cotización · {tipo?.nombre ?? 'Crédito'}</div>
              <div style={{ fontSize: 11.5, opacity: .9, marginTop: 2 }}>{asociado ? `Para ${asociado.nombre_completo}` : 'Estimación informativa'}</div>
            </div>
            <div className="no-print" style={{ display: 'inline-flex', gap: 8 }}>
              <button onClick={printPropuesta} disabled={!plan} title="Generar propuesta formal (PDF)"
                style={{ all: 'unset', cursor: plan ? 'pointer' : 'not-allowed', opacity: plan ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: 'var(--brand-deep)', padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 800 }}>
                <FileText size={14} weight="bold" /> Propuesta formal
              </button>
              <button onClick={() => window.print()} title="Imprimir tabla rápida"
                style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.18)', padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700 }}>
                <Printer size={14} weight="bold" /> Imprimir
              </button>
            </div>
          </div>

          {!plan ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Ajustá monto y plazo para ver la cotización.</div>
          ) : (
            <>
              {/* Hero */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: 'var(--hairline)' }}>
                {[
                  { k: redondea ? 'Pago mensual' : 'Cuota mensual', v: fmtQ0(cuotaMensual), main: true },
                  { k: 'Total a pagar', v: fmtQ0(totalCobrado) },
                  { k: 'N.º de cuotas', v: String(plan.num_cuotas) },
                  ...(redondea ? [{ k: 'De eso a aportaciones', v: fmtQ(plan.total_aporte_aportaciones ?? 0) }] : []),
                ].map((c) => (
                  <div key={c.k} style={{ background: c.main ? 'var(--brand-50, #fff3ec)' : '#fff', padding: '16px 20px' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted-foreground)' }}>{c.k}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, color: c.main ? 'var(--brand-deep)' : 'var(--ink)', fontFeatureSettings: '"tnum"' }}>{c.v}</div>
                  </div>
                ))}
              </div>

              {/* Meta */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 34px', padding: '16px 24px', borderBottom: '1px solid var(--hairline)' }}>
                {[
                  ['Monto', fmtQ0(monto)],
                  ['Tasa', tasaTxt],
                  ['Intereses', fmtQ(plan.total_interes)],
                  ['Primera cuota', fmtFecha(plan.fecha_primera_cuota)],
                  ['Última cuota', fmtFecha(plan.fecha_ultima_cuota)],
                ].map(([k, v]) => (
                  <div key={k}><div style={{ fontSize: 10, fontWeight: 800, letterSpacing: .8, textTransform: 'uppercase', color: 'var(--muted-foreground)' }}>{k}</div><div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 3, fontFeatureSettings: '"tnum"' }}>{v}</div></div>
                ))}
              </div>

              {/* Tabla de amortización */}
              <div style={{ padding: '6px 12px 12px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      {['N.º', 'Vence', 'Capital', 'Interés', 'Cuota', ...(redondea ? ['Aporte'] : []), 'Saldo'].map((h, i) => (
                        <th key={h} style={{ textAlign: i < 2 ? 'left' : 'right', padding: '10px 12px', fontSize: 10, fontWeight: 800, letterSpacing: .5, textTransform: 'uppercase', color: 'var(--brand-deep)', background: 'var(--brand-50, #fff3ec)', borderBottom: '1.5px solid var(--brand-100, var(--hairline))' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plan.cuotas.map((c) => (
                      <tr key={c.numero}>
                        <td style={tdL}>{c.numero}</td>
                        <td style={tdL}>{fmtFecha(c.fecha)}</td>
                        <td style={td}>{fmtQ(c.capital)}</td>
                        <td style={td}>{fmtQ(c.interes)}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{fmtQ0(c.cuota_cobrada ?? c.total)}</td>
                        {redondea && <td style={td}>{fmtQ(c.aporte_aportaciones ?? 0)}</td>}
                        <td style={td}>{fmtQ(Math.max(c.saldo_post, 0))}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#fafafa', fontWeight: 800 }}>
                      <td style={tdL}>—</td><td style={tdL}>Totales</td>
                      <td style={td}>{fmtQ(plan.total_capital)}</td>
                      <td style={td}>{fmtQ(plan.total_interes)}</td>
                      <td style={td}>{fmtQ0(totalCobrado)}</td>
                      {redondea && <td style={td}>{fmtQ(plan.total_aporte_aportaciones ?? 0)}</td>}
                      <td style={td}>Q0.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ padding: '4px 24px 20px', fontSize: 10.5, color: 'var(--ink-soft)', lineHeight: 1.6 }}>
                Cotización informativa, no vinculante. Sujeta a evaluación y aprobación.{redondea ? ' La cuota se cobra redondeada al quetzal; el excedente se abona a las aportaciones del asociado.' : ''} Cooperativa Integral de Ahorro y Crédito NEO, R.L. · NIT 120719290.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 14, padding: '16px 18px', boxShadow: 'var(--shadow-sm)' }
const lbl: React.CSSProperties = { fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--muted-foreground)', margin: '0 0 10px' }
const td: React.CSSProperties = { padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #f2f1ef', fontFeatureSettings: '"tnum"' }
const tdL: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #f2f1ef' }
